import {
  ConfigData,
  CountryConfigData,
  PricingFlowData,
  PrototypePageData,
  QuotePageData,
} from '@cpak/adp-cpak-bff-service-models';
import {
  CurrencyConversionUtils,
  PakPartPricing,
  PakPricingLibV1,
  PakPricingModel,
  PartPricingImportTypes,
} from '@cpak/adp-cpak-pricing-service-shared';
import { BOMPageData, InputForPricingCalculation } from '@cpak/interfaces';
import Decimal from 'decimal.js';
import { deepCopy, getQuoteRelations, addPartIdToBOM } from './utils';
import { LocationTypes, BOMModel } from '@cpak/adp-cpak-quotes-service-models';
import { LocalStorageService } from '@cpak/main-features-lib/services';
import { PRICING_BREAKDOWN_NUMERIC_FIELDS } from 'libs/main-features-lib/src/pak/interfaces/pricing-table';

export class PricingUtils {
  static getPricingModelFromPageData(
    quoteId: string,
    pageData?: QuotePageData | BOMPageData | PrototypePageData,
    shouldBeConverted = false
  ): PakPricingModel | undefined {
    const pricingModelFromPageData = pageData?.pricing?.find(pricing => pricing.pakId === quoteId);
    return shouldBeConverted && pricingModelFromPageData
      ? CurrencyConversionUtils.convertBaseToLocalCurrency(
          deepCopy(pricingModelFromPageData),
          new Decimal(pricingModelFromPageData.countryPricing.exchangeRate)
        )
      : pricingModelFromPageData;
  }

  static isPackPricingModelChanged(
    receivedPackPricingModel: PakPricingModel | null,
    currentPackPricingModel: PakPricingModel | null
  ): boolean {
    return (
      !!receivedPackPricingModel &&
      !!currentPackPricingModel &&
      !!currentPackPricingModel.total &&
      !!receivedPackPricingModel.total &&
      (currentPackPricingModel.total.price.toFixed(2) !== receivedPackPricingModel.total.price.toFixed(2) ||
        (currentPackPricingModel.rebate || 0) !== (receivedPackPricingModel.rebate || 0) ||
        currentPackPricingModel.transportationType !== receivedPackPricingModel.transportationType ||
        (currentPackPricingModel.affiliateAddedCost?.countryMsp || 0) !==
          (receivedPackPricingModel.affiliateAddedCost?.countryMsp || 0))
    );
  }

  static getUpdatedPricingModel(
    pricingModel: PakPricingModel,
    quoteData: QuotePageData | BOMPageData,
    shouldBeConverted: boolean,
    locationToTypeMap: Map<string, LocationTypes>,
    plantCountryId: string,
    bom: BOMModel,
    changedValues: InputForPricingCalculation
  ): PakPricingModel {
    const changedValuesInBaseCurrency = { ...changedValues };

    if (shouldBeConverted) {
      const exchangeRate = Decimal.div(1, new Decimal(pricingModel.countryPricing.exchangeRate));
      const msp = changedValues.affiliateAddedCostCountryMsp;
      const totalPrice = changedValues.totalPrice;
      if (msp) {
        changedValuesInBaseCurrency.affiliateAddedCostCountryMsp = Decimal.mul(msp, exchangeRate).toNumber();
      }
      if (totalPrice) {
        changedValuesInBaseCurrency.totalPrice = Decimal.mul(totalPrice, exchangeRate).toNumber();
      }
    }

    const pricingModelInBaseCurrency = PakPricingLibV1.calculate({
      pakId: pricingModel.pakId,
      pakPricings: PricingUtils.getUpdatedQuoteDataPricing(pricingModel, quoteData.pricing!, shouldBeConverted),
      locationToTypeMap,
      plantCountryId,
      quoteRelations: getQuoteRelations(quoteData),
      updatedInput: {
        bom: addPartIdToBOM(bom),
        ...changedValuesInBaseCurrency,
      },
    });

    return shouldBeConverted
      ? CurrencyConversionUtils.convertBaseToLocalCurrency(
          deepCopy(pricingModelInBaseCurrency),
          new Decimal(pricingModel.countryPricing.exchangeRate)
        )
      : pricingModelInBaseCurrency;
  }

  //Uncomment the following function if you want to use Catalog Number instead of Manufacturing Number
  // static getPartPricing(pricingModel: PakPricingModel, catalogNumber: string): PakPartPricing | undefined {
  //   return pricingModel.partPricings!.find(partPricing => partPricing.partPricing.catalogNumber === catalogNumber);
  // }

  static getPartPricing(pricingModel: PakPricingModel, mfgNumber: string): PakPartPricing | undefined {
    return pricingModel.partPricings!.find(partPricing => partPricing.partPricing.mfgNumber === mfgNumber);
  }

  static getPricingFlow(countries: CountryConfigData[], countryId?: string): string {
    return countries.find(country => country.id === countryId)?.pricingFlow || '';
  }

  static getUpdatedQuoteDataPricing(
    pakPricingModel: PakPricingModel,
    quoteDataPricing: PakPricingModel[],
    shouldBeConverted = false
  ): PakPricingModel[] {
    return quoteDataPricing.map(quoteDataPricing => {
      if (quoteDataPricing.pakId !== pakPricingModel.pakId) {
        return deepCopy(quoteDataPricing);
      } else {
        return shouldBeConverted
          ? CurrencyConversionUtils.convertBaseToLocalCurrency(
              deepCopy(pakPricingModel),
              Decimal.div(1, new Decimal(pakPricingModel.countryPricing.exchangeRate))
            )
          : deepCopy(pakPricingModel);
      }
    });
  }

  static getBaseCurrency(config: ConfigData, countryId?: string): string {
    return (
      config.plantConfig.find(plant => plant.countryIds.find(id => id === countryId))?.currency ||
      config.plantConfig.find(plant => plant.plant === config.defaultPlant)?.currency ||
      ''
    );
  }

  static getLocalCurrency(config: ConfigData, countryId?: string): string {
    return config.countries.find(country => country.id === countryId)?.currency || '';
  }

  static getExchangeRateFromPageData(pakId: string = '', pageData?: QuotePageData | PrototypePageData): number {
    return pageData?.pricing?.find(pricing => pricing.pakId === pakId)?.countryPricing.exchangeRate || -1;
  }

  static isCountryUsesCPQ(pricingFlow: string): boolean {
    return pricingFlow === PricingFlowData.cpqFrameworks || pricingFlow === PricingFlowData.cpqMal;
  }

  static isCountryUsesSalesforce(pricingFlow: string): boolean {
    return pricingFlow === PricingFlowData.crm;
  }

  static getPartSearchTerm(
    partPricingImportType: PartPricingImportTypes,
    localStorageService: LocalStorageService,
    countryId: string
  ): string {
    if (partPricingImportType === PartPricingImportTypes.countryGuidelines) {
      const guidelinesSearchTermsArray: [string, string][] =
        localStorageService.getItem('guidelines-search-term') || [];

      const guidelinesSearchTermsMap = new Map(guidelinesSearchTermsArray.map(item => [item[0], item[1]]));
      return guidelinesSearchTermsMap.get(countryId) as string;
    } else if (partPricingImportType === PartPricingImportTypes.costs) {
      return localStorageService.getItem('part-costs-search-term');
    } else {
      return '';
    }
  }

  static getPartPricingForMainLocations(
    pricingModel: PakPricingModel,
    mfgNumber: string
  ): PakPartPricing[] | undefined {
    return pricingModel.partPricings!.filter(
      partPricing =>
        partPricing.partPricing.mfgNumber === mfgNumber && partPricing.partPricing.locationType === LocationTypes.main
    );
  }

  static getPartPricingForLocation(
    pricingModel: PakPricingModel,
    mfgNumber: string,
    locationId: string
  ): PakPartPricing | undefined {
    return pricingModel.partPricings!.find(
      partPricing =>
        partPricing.partPricing.mfgNumber === mfgNumber &&
        partPricing.partPricing.locationType?.toString() === locationId
    );
  }

  static consolidatePartPricingData(partPricings: PakPartPricing[]): PakPartPricing {
    if (partPricings.length === 0) {
      throw new Error('Cannot consolidate empty array');
    }

    // Filter out 'addAfter' location types
    const filtered = partPricings.filter(p => p.partPricing.locationType !== LocationTypes.addAfter);
    // Use the first entry as the base
    const consolidated = JSON.parse(JSON.stringify(filtered[0]));

    for (let i = 1; i < filtered.length; i++) {
      const current = filtered[i];
      for (const field of PRICING_BREAKDOWN_NUMERIC_FIELDS) {
        consolidated.pricingBreakdown[field] =
          (consolidated.pricingBreakdown[field] || 0) + (current.pricingBreakdown[field] || 0);
      }
      // Sum levels array
      if (consolidated.pricingBreakdown.levels && current.pricingBreakdown.levels) {
        consolidated.pricingBreakdown.levels = consolidated.pricingBreakdown.levels.map(
          (level: number, idx: number) => (level || 0) + (current.pricingBreakdown.levels[idx] || 0)
        );
      }
    }

    return consolidated;
  }
}
